import { calculateSubscriptionPlans } from '../utils/subscriptionCalculator';
import { convertUSDToSubscriptionAmount } from '../utils/currencyUtils';
import { sortProductsByPriority } from '../utils/snapshotUtils';

// Єдине джерело правди для eligibility 360-day знижки.
// Використовується активним builder-ом і неактивним шляхом у usePricingSnapshot.js.
export const SUBSCRIPTION_ELIGIBLE_PRODUCT_TYPES = [
  'business-phone',
  'fmc',
  'call-tracking',
  'callback',
  'omnichannel-chat',
  'ai-conversation',
];

export function getSnapshotProductType(item) {
  return item.productType ||
    (item.label?.includes('Business Phone') ? 'business-phone' :
    item.label?.includes('FMC') ? 'fmc' :
    item.label?.includes('Call Tracking') ? 'call-tracking' :
    item.label?.includes('Callback') ? 'callback' :
    item.label?.includes('Chat') ? 'omnichannel-chat' : 'other');
}

export function buildOneTimePayments({
  t,
  region,
  currencyRates,
  phoneNumbers,
  enablePhoneActivation,
  isProductSelected,
  eniqAiImplementationFee,
  enableEniqImplementationFee,
  voicebotImplementationFee,
  enableVoicebotImplementationFee,
}) {
  const oneTimePayments = [];

  if (enablePhoneActivation && isProductSelected && isProductSelected('phone-numbers') && phoneNumbers && phoneNumbers.length > 0) {
    const totalActivationFee = phoneNumbers.reduce((sum, phone) => (
      sum + (phone.quantity * (phone.activationFee || 0))
    ), 0);

    if (totalActivationFee > 0) {
      oneTimePayments.push({
        description: t('calculator.phoneNumberActivation', 'Phone numbers activation'),
        total: totalActivationFee,
        hasActivation: true,
      });
    }
  }

  if (enableEniqImplementationFee && isProductSelected && isProductSelected('eniq-ai') && eniqAiImplementationFee > 0) {
    oneTimePayments.push({
      description: t('calculator.eniqImplementationFee', 'Eniq AI Implementation'),
      total: eniqAiImplementationFee,
      hasActivation: true,
    });
  }

  if (enableVoicebotImplementationFee && isProductSelected && isProductSelected('voicebot') && voicebotImplementationFee > 0) {
    oneTimePayments.push({
      description: t('calculator.voicebotImplementationFeeLabel', 'VoiceBot Implementation'),
      total: voicebotImplementationFee,
      hasActivation: true,
    });
  }

  const totalOneTimePayments = oneTimePayments.reduce(
    (sum, payment) => sum + convertUSDToSubscriptionAmount(payment.total, region, currencyRates),
    0
  );

  return { oneTimePayments, totalOneTimePayments };
}

export function buildActiveTabSnapshot({
  t,
  tabData,
  summaryItemsForSubscription,
  summaryItems,
  total,
  currency,
  region,
  customDiscount360,
  customDiscount180,
  currencyRates,
  oneTimePayments,
  totalOneTimePayments,
  now = () => new Date().toISOString(),
}) {
  const tabSettings = tabData.settings?.subscriptionSettings || {};
  const tabSubscriptionOptions = {
    show360Days: true,
    show180Days: tabSettings.subscription180Days || false,
    show90Days: tabSettings.subscription90Days || false,
    show12Month: (tabSettings.subscription12Month || false) && total >= 250,
    // Split-payment is UA-only, but users can still hide it via the subscription
    // settings dialog — respect that toggle so disabled plans don't reappear in
    // the generated offer.
    showSplitPayment: region === 'ua' && tabSettings.subscriptionSplitPayment !== false,
  };

  const subscriptionData = calculateSubscriptionPlans(
    summaryItemsForSubscription || [],
    tabSubscriptionOptions,
    region,
    total,
    summaryItems,
    customDiscount360,
    customDiscount180
  );

  const subscription_plans = subscriptionData.plans.map(plan => ({
    ...plan,
    paymentAmount: plan.paymentAmount + totalOneTimePayments,
  }));

  const discountFactor = 1 + (customDiscount360 / 100);
  const individual_prices = sortProductsByPriority(summaryItems.map(item => {
    const productType = getSnapshotProductType(item);
    const isEligible = SUBSCRIPTION_ELIGIBLE_PRODUCT_TYPES.includes(productType);

    return {
      ...item,
      price: isEligible ? item.price / discountFactor : item.price,
      productType,
    };
  }));

  return {
    individual_prices,
    subscription_plans,
    total_monthly: subscriptionData.defaultPlan?.monthlyPrice || total,
    one_time_payments: oneTimePayments,
    currency,
    region,
    currencyRates,
    custom_discount_360: customDiscount360,
    custom_discount_180: customDiscount180,
    calculated_at: now(),
  };
}
